import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { buildFilterKey } from '@/services/filterEngine'
import {
  getResponseColor,
  getEthnicityColorByName,
  getTribeColorByName,
  getProvinceColor,
  getUnionColor,
} from '@/utils/colors'

// Financial situation order (least wealthy → most wealthy) with blue gradient (light → dark)
const FINANCIAL_ORDER = [
  'Falling behind on monthly expenses, in debt',
  'Making ends meet, but with no savings',
  'Getting by, with some savings',
  'Financially secure',
  'Well-off',
]

const FINANCIAL_COLORS: Record<string, string> = {
  'Falling behind on monthly expenses, in debt': '#bfdbfe', // lightest blue
  'Making ends meet, but with no savings': '#93c5fd',       // light blue
  'Getting by, with some savings': '#3b82f6',               // medium blue
  'Financially secure': '#1d4ed8',                          // dark blue
  'Well-off': '#1e3a8a',                                    // darkest blue
}

// All demographic questions - labels from topline report
const ALL_DEMO_QUESTIONS = [
  { id: 'Demo_Age', label: 'Age?', breakId: 'age' },
  { id: 'Demo_Gender', label: 'Gender?', breakId: 'gender' },
  { id: 'Demo_Genderation', label: 'Gender + Age', breakId: 'genderation' },
  { id: 'Demo_Financial Situation', label: 'Which phrase below best describes your current financial situation?', breakId: 'financial' },
  { id: 'Demo_Education Level', label: 'Please indicate the highest level of education you have completed.', breakId: 'education' },
  { id: 'Demo_Rural/ Urban', label: 'Rural/ Urban', breakId: 'zone' },
  { id: 'Demo_Region', label: 'In which region do you live?', breakId: 'region' },
  { id: 'Demo_Union Membership', label: 'Are you currently represented in the workplace by a union?', breakId: 'union' },
  { id: 'Demo_Ethnicity', label: 'How would you describe your ethnic background? UNAIDED', breakId: 'ethnicity' },
  { id: 'Demo_Thought Tribes', label: 'Thought Tribes', breakId: 'tribes' },
]

export function DemographicsGeneral() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Demographics - General
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">

        <div className="space-y-8">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            ALL_DEMO_QUESTIONS.filter(q => q.breakId !== selectedBreak).map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey(
                  'total',
                  selectedBreak,
                  isBreakActive ? value : undefined,
                  selectedProvince
                )
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
                  questionData = question.data[fallbackKey]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              const isEthnicity = id === 'Demo_Ethnicity'
              const isTribes = id === 'Demo_Thought Tribes'
              const isRegion = id === 'Demo_Region' || id === 'Demo_Province'
              const isUnion = id === 'Demo_Union Membership'
              const isFinancial = id === 'Demo_Financial Situation'

              const getColorForLabel = (lbl: string, _idx: number) => {
                if (isEthnicity) return getEthnicityColorByName(lbl)
                if (isTribes) return getTribeColorByName(lbl)
                if (isRegion) return getProvinceColor(lbl)
                if (isUnion) return getUnionColor(lbl)
                if (isFinancial) return FINANCIAL_COLORS[lbl] || '#3b82f6'
                return getResponseColor('demographic', lbl)
              }

              // Sort financial responses in order
              const sortResponses = (responses: typeof rows[0]['data']['responses']) => {
                if (!isFinancial) return responses
                return [...responses].sort((a, b) => {
                  const aIdx = FINANCIAL_ORDER.indexOf(a.label)
                  const bIdx = FINANCIAL_ORDER.indexOf(b.label)
                  if (aIdx === -1) return 1
                  if (bIdx === -1) return -1
                  return aIdx - bIdx
                })
              }

              const needsCustomColor = isEthnicity || isTribes || isRegion || isUnion || isFinancial

              // Get legend labels (sorted if financial)
              const legendLabels = sortResponses(rows[0]?.data?.responses || []).map(r => r.label)

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                    {label}
                  </h3>

                  <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
                    {legendLabels.map((lbl, idx) => (
                      <div key={lbl} className="flex items-center gap-1 text-xs" title={lbl}>
                        <div
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: getColorForLabel(lbl, idx) }}
                        />
                        <span className="text-slate-600 dark:text-slate-400">
                          {lbl}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div
                          className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0"
                          title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        >
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortResponses(row.data!.responses)}
                            scaleType="demographic"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            colorGetter={needsCustomColor ? getColorForLabel : undefined}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
